import React, { useState } from "react";
import { Scissors, Phone, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";

const LANGS = { en: "English", hi: "हिन्दी", gu: "ગુજરાતી" };

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { login } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [business, setBusiness] = useState("");
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mockOtp, setMockOtp] = useState("");

  const sendOtp = async () => {
    if (phone.trim().length < 10) { toast.error(t("phonePlaceholder")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      setMockOtp(data.mock_otp);
      setIsExistingUser(!!data.user_exists);
      setOtp("");
      setName("");
      setBusiness("");
      setStep("otp");
      toast.success(`${t("sendOtp")} · OTP: ${data.mock_otp}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", {
        phone,
        otp,
        name: isExistingUser ? null : (name || null),
        business_name: isExistingUser ? null : (business || null),
      });
      login(data.token, data.user);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* soft textile backdrop */}
      <div
        className="absolute inset-0 opacity-[0.09] bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/37975931/pexels-photo-37975931.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/70 to-background" />

      <div className="relative max-w-md mx-auto px-5 pt-12 pb-16 flex flex-col min-h-screen">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-primary text-primary-foreground grid place-items-center shadow-sm">
              <Scissors size={20} />
            </span>
            <div>
              <div className="font-heading font-bold text-xl">StitchQuote</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Surat · Embroidery</div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-10" data-testid={TID.langSwitcher}>{lang.toUpperCase()}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup value={lang} onValueChange={setLang}>
                {Object.entries(LANGS).map(([k, label]) => (
                  <DropdownMenuRadioItem key={k} value={k} data-testid={`lang-opt-${k}`}>{label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-extrabold leading-tight text-secondary">
            {t("tagline")}
          </h1>
          <p className="mt-3 text-muted-foreground text-base">
            {t("phoneHelp")}
          </p>
        </div>

        <div className="mt-8 sq-card p-6">
          {step === "phone" && (
            <div className="space-y-4">
              <Label className="sq-label">{t("phonePlaceholder")}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  data-testid={TID.phoneInput}
                  className="h-14 pl-10 text-lg"
                  placeholder="+91 98765 43210"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <Button
                data-testid={TID.sendOtpBtn}
                onClick={sendOtp}
                disabled={loading}
                className="h-14 w-full text-lg font-semibold sq-tap"
              >
                {loading ? "…" : t("sendOtp")}
              </Button>
            </div>
          )}

          {step === "otp" && (
            <div className="space-y-4">
              <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => { setStep("phone"); setIsExistingUser(false); }} data-testid={TID.changePhoneBtn}>
                <ArrowLeft size={14} /> {t("changePhone")} · {phone}
              </button>

              <p className="text-sm text-muted-foreground">
                {isExistingUser ? t("welcomeBack") : t("completeProfile")}
              </p>

              <div>
                <Label className="sq-label">{t("enterOtp")}</Label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    data-testid={TID.otpInput}
                    className="h-14 pl-10 text-lg tracking-[0.4em] font-semibold"
                    placeholder="123456"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                {mockOtp && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    {t("otpHint")} · <span className="font-mono text-foreground">{mockOtp}</span>
                  </div>
                )}
              </div>

              {!isExistingUser && (
                <>
                  <div>
                    <Label className="sq-label">{t("yourName")}</Label>
                    <Input data-testid={TID.nameInput} className="h-12 mt-2" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div>
                    <Label className="sq-label">{t("businessName")}</Label>
                    <Input data-testid={TID.businessInput} className="h-12 mt-2" value={business} onChange={(e) => setBusiness(e.target.value)} />
                  </div>
                </>
              )}

              <Button
                data-testid={TID.verifyOtpBtn}
                onClick={verify}
                disabled={loading || otp.length !== 6}
                className="h-14 w-full text-lg font-semibold sq-tap"
              >
                {loading ? "…" : t("verify")}
              </Button>
            </div>
          )}
        </div>

        <div className="mt-auto pt-10 text-center text-xs text-muted-foreground">
          Built for embroidery units in Surat, India.
        </div>
      </div>
    </div>
  );
}
